import React from 'react'
import { Helmet } from 'react-helmet'

import Header from '../components/header'
import { headerProps } from '../components/HeaderConfig'
import Footer from '../components/footer'
import ThemeToggle from '../components/theme-toggle'
import useTheme from '../hooks/useTheme'
import useScrollAnimation from '../hooks/useScrollAnimation'
import './projects.css'

const AnimatedDiv = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.05 })
  return (
    <div
      ref={ref}
      className={`proj-animated ${isVisible ? 'proj-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

const projects = [
  {
    title: 'Manasara AI',
    tagline: 'AI-Powered Building Code Compliance',
    badge: 'Y Combinator Top 5%',
    year: '2024',
    description:
      'Designed and developed an AI-powered platform automating building code compliance checks, integrating with BIM tools (Revit, Archicad, Rhino) and reducing design validation time by 60%. Fine-tuned LLaMA models with RAG techniques achieving 85% accuracy in interpreting complex building codes.',
    tech: ['React', 'Three.js', 'AWS', 'Python', 'LLaMA', 'RAG'],
    image: '/screenshot%202025-06-15%20at%2011.45.05%C3%A2%C2%80%C2%AFam-300h.png',
    link: 'https://manasara-ai.com/',
    highlights: [
      'Built web-based 3D BIM viewer with interactive compliance visualization',
      'Scalable AWS backend (Amplify, Lambda, DynamoDB, SageMaker)',
      'Integrates with Revit, Grasshopper, Archicad, and Rhino',
    ],
  },
  {
    title: 'Geometry Factory',
    tagline: 'Interactive 3D Geometry Configuration Platform',
    badge: 'Personal Project',
    year: '2025',
    description:
      'A web-based interactive platform for designing and configuring 3D geometric assemblies in real-time. Enables architects, engineers, and designers to explore parametric configurations, visualize prefabricated building components, and collaborate on assembly design through an intuitive browser-based interface.',
    tech: ['Three.js', 'React', 'JavaScript', 'WebGL', '3D Visualization'],
    image: '/prefab%20software-300h.png',
    link: 'https://abhipalit3.github.io/Geometry-Factory/',
    highlights: [
      'Real-time 3D parametric configuration and visualization',
      'Browser-based — no installation required',
      'Streamlined multi-stakeholder collaborative design workflows',
    ],
  },
  {
    title: 'Generative Urban Design',
    tagline: 'Computational Neighborhood Layout Optimization',
    badge: 'Best Master\'s Thesis — IIT Roorkee',
    year: '2021',
    description:
      'Developed generative design algorithms for optimizing urban neighborhood spatial layouts. The system generates multi-variate design scenarios based on stakeholder-specified goals, evaluating solutions against profitability, open space, amenity proximity, and outdoor comfort metrics.',
    tech: ['Dynamo', 'Python', 'Autodesk Revit', 'Generative Design'],
    image: '/logos/generative%20urban%20design.gif',
    link: 'https://github.com/abhipalit3/Generative-Urban-Design-Algorithm',
    highlights: [
      'Optimizes 4 objectives: plot count, green space, amenity distance, shading',
      'Generates top 10 ranked layouts via normalized performance scoring',
      'Applied to Salt Lake City, Kolkata — outperformed existing designs',
    ],
  },
]

const Projects = () => {
  const { isDark, toggleTheme } = useTheme()

  return (
    <div className="projects-container">
      <Helmet>
        <title>Projects - Abhishek Palit | AI, BIM & Construction Technology</title>
        <meta property="og:title" content="Projects - Abhishek Palit" />
        <meta
          name="description"
          content="Explore projects including Manasara AI (Y Combinator), prefabrication platforms, generative urban design, and sustainable construction solutions."
        />
      </Helmet>
      <Header
        {...headerProps}
        rootClassName="headerroot-class-name3"
        themeToggle={<ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />}
      />

      <div className="proj-page">
        {/* Hero */}
        <div className="proj-hero">
          <span className="proj-label">Projects</span>
          <h1 className="proj-hero-title">
            Selected work in construction<br />
            technology & design.
          </h1>
          <p className="proj-hero-subtitle">
            A collection of tools, platforms, and research projects advancing the built environment
            through AI, generative design, and digital construction.
          </p>
          <div className="proj-hero-stats">
            <div className="proj-stat">
              <span className="proj-stat-number">{projects.length}</span>
              <span className="proj-stat-label">Projects</span>
            </div>
            <div className="proj-stat-divider" />
            <div className="proj-stat">
              <span className="proj-stat-number">{[...new Set(projects.flatMap(p => p.tech))].length}+</span>
              <span className="proj-stat-label">Technologies</span>
            </div>
            <div className="proj-stat-divider" />
            <div className="proj-stat">
              <span className="proj-stat-number">2</span>
              <span className="proj-stat-label">Awards</span>
            </div>
          </div>
        </div>

        {/* Project Cards */}
        <div className="proj-list">
          {projects.map((project, i) => (
            <AnimatedDiv key={i} className="proj-card-wrapper" delay={i * 100}>
              <a
                href={project.link}
                target="_blank"
                rel="noreferrer noopener"
                className="proj-card-link"
              >
                <div className="proj-card">
                  {/* Image */}
                  {project.image ? (
                    <div className="proj-card-image">
                      <img src={project.image} alt={project.title} />
                    </div>
                  ) : (
                    <div className="proj-card-image proj-card-image-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                    </div>
                  )}

                  {/* Content */}
                  <div className="proj-card-content">
                    <span className="proj-card-number">0{i + 1}</span>
                    <div className="proj-card-top">
                      <div className="proj-card-meta">
                        <span className="proj-card-badge">{project.badge}</span>
                        <span className="proj-card-year">{project.year}</span>
                      </div>
                      <h2 className="proj-card-title">{project.title}</h2>
                      <p className="proj-card-tagline">{project.tagline}</p>
                    </div>

                    <p className="proj-card-desc">{project.description}</p>

                    {/* Highlights */}
                    <ul className="proj-card-highlights">
                      {project.highlights.map((h, j) => (
                        <li key={j}>{h}</li>
                      ))}
                    </ul>

                    {/* Tech Tags */}
                    <div className="proj-card-tech">
                      {project.tech.map((t, j) => (
                        <span key={j} className="proj-tech-tag">{t}</span>
                      ))}
                    </div>

                    {/* CTA */}
                    <span className="proj-card-cta">
                      {project.link.includes('github.com/') ? 'View on GitHub' : 'View project'}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 17L17 7M7 7h10v10" />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            </AnimatedDiv>
          ))}
        </div>
      </div>

      <Footer rootClassName="footerroot-class-name5"></Footer>
    </div>
  )
}

export default Projects
